// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getSessionUser } from '@/lib/authSession';

export interface SportsHubMessage {
  id: string;
  title: string;
  message: string;
  message_type: 'info' | 'warning' | 'success' | 'error' | 'announcement';
  sport_id: string | null;
  is_global: boolean;
  is_active: boolean;
  display_order: number;
  starts_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  sport?: {
    id: string;
    name: string;
    gender: string | null;
  };
}

export const useSportsHubMessages = () => {
  const [messages, setMessages] = useState<SportsHubMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('sports_hub_messages')
        .select(`
          *,
          sport:sports(id, name, gender)
        `)
        .order('display_order')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setMessages(data || []);
    } catch (err) {
      console.error('Error fetching sports hub messages:', err);
      setError('Failed to load messages');
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);        
    }
  };

  const createMessage = async (messageData: Partial<SportsHubMessage>) => {
    try {
      const { data, error } = await supabase
        .from('sports_hub_messages')
        .insert([{
          ...messageData,
          created_by: (await getSessionUser()).data.user?.id
        }])
        .select(`
          *,
          sport:sports(id, name, gender)
        `)
        .single();

      if (error) throw error;

      setMessages(prev => [data, ...prev]);
      toast.success('Message created successfully');
      return data;
    } catch (err) {
      console.error('Error creating message:', err);
      toast.error('Failed to create message');
      throw err;
    }
  };

  const updateMessage = async (id: string, messageData: Partial<SportsHubMessage>) => {
    try {
      const { data, error } = await supabase
        .from('sports_hub_messages')
        .update(messageData)
        .eq('id', id)
        .select(`
          *,
          sport:sports(id, name, gender)
        `)
        .single();

      if (error) throw error;

      setMessages(prev => prev.map(msg => msg.id === id ? data : msg));
      toast.success('Message updated successfully');
      return data;
    } catch (err) {
      console.error('Error updating message:', err);
      toast.error('Failed to update message');
      throw err;
    }
  };

  const deleteMessage = async (id: string) => {
    try {
      const { error } = await supabase
        .from('sports_hub_messages')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMessages(prev => prev.filter(msg => msg.id !== id));
      toast.success('Message deleted successfully');
    } catch (err) {
      console.error('Error deleting message:', err);
      toast.error('Failed to delete message');
      throw err;
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  return {
    messages,
    loading,
    error,
    fetchMessages,
    createMessage,
    updateMessage,
    deleteMessage
  };
};

// Hook for fetching messages for public display (active messages only)
export const usePublicSportsHubMessages = (sportId?: string) => {
  const [messages, setMessages] = useState<SportsHubMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPublicMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('sports_hub_messages')
        .select(`
          *,
          sport:sports(id, name, gender)
        `)
        .eq('is_active', true)
        .order('display_order')
        .order('created_at', { ascending: false });

      // If sportId is provided, get messages for that sport + global messages
      if (sportId) {
        query = query.or(`sport_id.eq.${sportId},is_global.eq.true`);
      } else {
        // If no sportId, only get global messages
        query = query.eq('is_global', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter messages based on start/end dates
      const now = new Date();
      const activeMessages = (data || []).filter(msg => {
        const startsAt = msg.starts_at ? new Date(msg.starts_at) : null;
        const expiresAt = msg.expires_at ? new Date(msg.expires_at) : null;
        
        if (startsAt && startsAt > now) return false;
        if (expiresAt && expiresAt < now) return false;
        
        return true;
      });

      setMessages(activeMessages);
    } catch (err) {
      console.error('Error fetching public sports hub messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicMessages();
  }, [sportId]);

  return {
    messages,
    loading,
    error,
    refetch: fetchPublicMessages
  };
};